import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaHome,
  FaHeart,
  FaUpload,
  FaUsers,
  FaBroadcastTower,
} from 'react-icons/fa';
import { MdLock, MdLockOpen, MdLocalHospital } from 'react-icons/md';
import UploadModal from '../UploadModal';
import { User } from '../../types/entities';
import useSidebar from './useSidebar';
import { DEMO_MODE } from '../../config';
import { useOriginAccess } from '../../hooks/useOriginAccess';
import styles from './Sidebar.module.css';

interface DesktopSidebarProps {
  currentUser?: User;
  className?: string;
  onOpenUploadModal?: () => void;
}

// メニューパスの型定義
type MenuPath = '/' | '/my-favorites' | '/my-videos' | '/admin' | '/live-streams';

/**
 * PC用サイドバーコンポーネント
 * 
 * @param props - DesktopSidebarコンポーネントのプロパティ
 * @returns DesktopSidebarコンポーネント
 */
const DesktopSidebar = ({ currentUser, className, onOpenUploadModal }: DesktopSidebarProps) => {
  const location = useLocation();
  const {
    isModalOpen,
    openModal,
    closeModal,
    handleUserManagement,
    isAdmin,
    selectedMyVideoStatus,
    toggleMyVideoDropdown
  } = useSidebar(currentUser);
  const handleOpenUploadModal = onOpenUploadModal ?? openModal;
  // 院外接続ではアップロード自体が backend で拒否されるため、入口（動線）を非表示にする。
  // 最終的なアクセス制御はサーバ(requireInternalNetwork)が担保（フロントは動線の出し分けのみ）。
  const { isExternal } = useOriginAccess();

  // 現在のパスに基づいてアクティブなメニューを判定（パフォーマンス最適化）
  const isActive = useMemo(() => (path: MenuPath) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
    // TODO: より堅牢なパス判定ロジックの実装
    // 例: /my-favorites/123 のような場合の完全一致チェック
  }, [location.pathname]);


  return (
    <div className={className || ''}>
      {/* PC用サイドバー */}
      <div className={styles['sidebar-desktop']}>
        <nav className={styles['navigation-desktop']}>
          <ul className={styles['menu-desktop']}>
            {/* グループ1: ホーム + お気に入り */}
            <li className={styles['menu-item-desktop']}>
              <div className={styles['menu-group-desktop']}>
                <Link
                  to="/"
                  className={`${styles['menu-link-desktop']} ${isActive('/') ? styles.active : ''}`}
                  aria-current={isActive('/') ? 'page' : undefined}
                >
                  <FaHome className={styles['icon-desktop']} />
                  <span className={styles['label-desktop']}>ホーム</span>
                </Link>
                <Link
                  to="/live-streams"
                  className={`${styles['menu-link-desktop']} ${isActive('/live-streams') ? styles.active : ''}`}
                  aria-current={isActive('/live-streams') ? 'page' : undefined}
                >
                  <FaBroadcastTower className={styles['icon-desktop']} />
                  <span className={styles['label-desktop']}>ライブ配信</span>
                </Link>
                <Link
                  to="/my-favorites"
                  className={`${styles['menu-link-desktop']} ${isActive('/my-favorites') ? styles.active : ''}`}
                  aria-current={isActive('/my-favorites') ? 'page' : undefined}
                >
                  <FaHeart className={styles['icon-desktop']} />
                  <span className={styles['label-desktop']}>お気に入り</span>
                </Link>
              </div>
            </li>
            <li aria-hidden="true"><hr className={styles['sidebar-divider']} /></li>
            {/* グループ2: マイビデオ + サブ項目 */}
            <li className={styles['menu-item-desktop']}>
              <div className={styles['menu-group-desktop']}>
                <div className={styles['section-header-desktop']}>マイビデオ</div>
                <Link
                  to="/my-videos?status=public"
                  className={`${styles['menu-link-desktop']} ${selectedMyVideoStatus === 'public' ? styles['submenu-link-active'] : ''}`}
                >
                  <MdLockOpen className={styles['icon-desktop']} />
                  <span className={styles['label-desktop']}>公開中</span>
                </Link>
                <Link
                  to="/my-videos?status=hospital_only"
                  className={`${styles['menu-link-desktop']} ${selectedMyVideoStatus === 'hospital_only' ? styles['submenu-link-active'] : ''}`}
                >
                  <MdLocalHospital className={styles['icon-desktop']} />
                  <span className={styles['label-desktop']}>院内限定公開</span>
                </Link>
                <Link
                  to="/my-videos?status=private"
                  className={`${styles['menu-link-desktop']} ${selectedMyVideoStatus === 'private' ? styles['submenu-link-active'] : ''}`}
                >
                  <MdLock className={styles['icon-desktop']} />
                  <span className={styles['label-desktop']}>非公開</span>
                </Link>
              </div>
            </li>
            {/* デモモード or 院外接続ではアップロードを無効化（UI非表示） */}
            {!DEMO_MODE && !isExternal && (
              <>
                <li aria-hidden="true"><hr className={styles['sidebar-divider']} /></li>
                <li className={styles['menu-item-desktop']}>
                  <button
                    className={styles['menu-link-desktop']}
                    onClick={handleOpenUploadModal}
                    data-testid="sidebar__upload-button"
                  >
                    <FaUpload className={styles['icon-desktop']} />
                    <span className={styles['label-desktop']}>アップロード</span>
                  </button>
                </li>
              </>
            )}
            {/* Admin専用：ユーザー管理メニュー */}
            {isAdmin && (
              <li className={styles['menu-item-desktop']}>
                <button
                  className={`${styles['menu-link-desktop']} ${isActive('/admin') ? styles.active : ''}`}
                  onClick={handleUserManagement}
                  aria-current={isActive('/admin') ? 'page' : undefined}
                >
                  <FaUsers className={styles['icon-desktop']} />
                  <span className={styles['label-desktop']}>ユーザー</span>
                </button>
              </li>
            )}
          </ul>
        </nav>
      </div>

      {!onOpenUploadModal && isModalOpen && (
        <UploadModal onClose={closeModal} onUploadStart={undefined} />
      )}
    </div>
  );
};

export default DesktopSidebar;
